import type { Recipe, WeekMeal, MealType, Rating } from '../types';
import { ComponentPills } from './ComponentPills';
import { PremadeChip } from './PremadeChip';
import { RatingButtons } from './RatingButtons';
import { EmojiBadge } from './EmojiBadge';

export function WeekGrid(props: {
  startDate: string;
  todayIso: string;
  enabledMealTypes: MealType[];
  meals: WeekMeal[];
  dates: string[];
  recipeById: (id: string) => Recipe | undefined;
  onRate: (date: string, mealType: MealType, recipeId: string, r: Rating) => void;
  onTapSlot: (date: string, mealType: MealType) => void;
  onClearSlot: (date: string, mealType: MealType, recipeId: string) => void;
  onOpenRecipe: (recipeId: string) => void;
}) {
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: `70px repeat(${props.dates.length}, 1fr)`, gap: 6,
    }}>
      <div />
      {props.dates.map(d => {
        const today = d === props.todayIso;
        const dn = new Date(d + 'T00:00:00Z').toLocaleDateString(undefined, { weekday: 'short', timeZone: 'UTC' });
        return (
          <div key={d} style={{
            padding: '8px 6px', textAlign: 'center',
            background: 'transparent', borderRadius: 10,
          }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--plum)' }}>{dn}</div>
            <div style={{
              fontSize: 11, color: 'var(--plum-soft)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
            }}>
              <span>{d.slice(5)}</span>
              {today && (
                <span style={{
                  display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
                  background: 'var(--pink-primary)',
                }} aria-label="today" />
              )}
            </div>
          </div>
        );
      })}

      {props.enabledMealTypes.map(mt => (
        <FragmentRow
          key={mt}
          mealType={mt}
          dates={props.dates}
          meals={props.meals}
          recipeById={props.recipeById}
          onRate={props.onRate}
          onTapSlot={props.onTapSlot}
          onClearSlot={props.onClearSlot}
          onOpenRecipe={props.onOpenRecipe}
        />
      ))}
    </div>
  );
}

function FragmentRow(props: {
  mealType: MealType;
  dates: string[];
  meals: WeekMeal[];
  recipeById: (id: string) => Recipe | undefined;
  onRate: (date: string, mealType: MealType, recipeId: string, r: Rating) => void;
  onTapSlot: (date: string, mealType: MealType) => void;
  onClearSlot: (date: string, mealType: MealType, recipeId: string) => void;
  onOpenRecipe: (recipeId: string) => void;
}) {
  return (
    <>
      <div className="label-caps" style={{ display: 'flex', alignItems: 'center', padding: 8 }}>{props.mealType}</div>
      {props.dates.map(d => {
        const slotMeals = props.meals.filter(m => m.date === d && m.meal_type === props.mealType && m.recipe_id);
        const hasMeals = slotMeals.length > 0;
        return (
          <div key={d}
            onClick={() => !hasMeals && props.onTapSlot(d, props.mealType)}
            style={{
              position: 'relative',
              background: hasMeals ? 'white' : 'var(--canvas)',
              borderRadius: 10, padding: hasMeals ? 6 : '9px 10px',
              minHeight: 96, display: 'flex',
              flexDirection: 'column',
              alignItems: hasMeals ? 'stretch' : 'center',
              justifyContent: hasMeals ? 'flex-start' : 'center',
              gap: 6,
              border: hasMeals ? '1px solid transparent' : '1px solid var(--hairline)',
              cursor: hasMeals ? 'default' : 'pointer',
              fontSize: 12, color: hasMeals ? 'var(--ink)' : 'var(--muted)',
            }}>
            {hasMeals ? (
              <>
                {slotMeals.map((meal, i) => {
                  const recipe = props.recipeById(meal.recipe_id!);
                  if (!recipe) return null;
                  return (
                    <div key={`${recipe.id}-${i}`} style={{
                      position: 'relative',
                      paddingTop: i === 0 ? 0 : 6,
                      borderTop: i === 0 ? 'none' : '1px solid var(--hairline)',
                      display: 'flex', gap: 8, alignItems: 'flex-start',
                    }}>
                      {/* Thumbnail */}
                      <button
                        type="button"
                        aria-label={`Open ${recipe.name}`}
                        onClick={e => { e.stopPropagation(); props.onOpenRecipe(recipe.id); }}
                        style={{
                          flexShrink: 0, width: 56, height: 56,
                          padding: 0, border: 'none', borderRadius: 8,
                          overflow: 'hidden', cursor: 'pointer',
                          background: 'transparent',
                        }}
                      >
                        <div style={{
                          width: 56, height: 56, borderRadius: 8, overflow: 'hidden',
                        }}>
                          <EmojiBadge
                            category={recipe.category}
                            cuisine={recipe.cuisine}
                            imageUrl={recipe.image_url}
                            imageFocus={recipe.image_focus}
                            height={56}
                          />
                        </div>
                      </button>
                      {/* Content */}
                      <div style={{
                        flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3,
                        paddingRight: 18,
                      }}>
                        <button
                          type="button"
                          onClick={e => { e.stopPropagation(); props.onOpenRecipe(recipe.id); }}
                          style={{
                            background: 'none', border: 'none', padding: 0, color: 'var(--ink)',
                            font: 'inherit', cursor: 'pointer', textAlign: 'left',
                            fontWeight: 700, lineHeight: 1.2, fontSize: 12.5,
                            overflow: 'hidden', textOverflow: 'ellipsis',
                            display: '-webkit-box', WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical' as any,
                          }}
                        >{recipe.name}</button>
                        <PremadeChip visible={recipe.is_make_ahead} />
                        <ComponentPills components={recipe.components} hiddenVeggie={recipe.hidden_veggie} />
                        <div style={{ marginTop: 'auto' }}>
                          <RatingButtons
                            value={meal.rating}
                            onChange={r => props.onRate(d, props.mealType, recipe.id, r)}
                          />
                        </div>
                      </div>
                      <button
                        type="button"
                        aria-label={`Remove ${recipe.name} from plan`}
                        onClick={e => { e.stopPropagation(); props.onClearSlot(d, props.mealType, recipe.id); }}
                        style={{
                          position: 'absolute', top: i === 0 ? -2 : 4, right: -2,
                          width: 20, height: 20, borderRadius: 'var(--radius-pill)',
                          background: 'var(--surface-card)', border: 'none', cursor: 'pointer',
                          color: 'var(--muted)', fontSize: 13, lineHeight: 1, padding: 0,
                        }}
                      >×</button>
                    </div>
                  );
                })}
                <button
                  type="button"
                  onClick={e => { e.stopPropagation(); props.onTapSlot(d, props.mealType); }}
                  style={{
                    background: 'none', border: 'none', padding: '2px 0',
                    color: 'var(--muted)', fontSize: 11, fontWeight: 500,
                    cursor: 'pointer', textAlign: 'left',
                  }}
                >+ Add another</button>
              </>
            ) : (
              <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--muted)' }}>
                + Add {props.mealType}
              </span>
            )}
          </div>
        );
      })}
    </>
  );
}
